var React = require('react');
var ReactDOM = require('react-dom');
var { createElement: h, useEffect, useRef, useState } = React;
var { JsonTreeNode } = require('./JsonTreeViewer');

var VAR_TYPE_LABELS = { string: 'Text', int: 'Num', date: 'Date', bool: 'Bool', json: 'Obj', array: 'List' };
var VAR_TYPE_COLORS = { string: 'vars-type-text', int: 'vars-type-num', date: 'vars-type-date', bool: 'vars-type-bool', json: 'vars-type-obj', array: 'vars-type-list' };

function normalizeVarType(value) {
  var raw = String(value || '').toLowerCase();
  if (raw === 'int' || raw === 'integer' || raw === 'number') return 'int';
  if (raw === 'date') return 'date';
  if (raw === 'bool' || raw === 'boolean') return 'bool';
  if (raw === 'json' || raw === 'object') return 'json';
  if (raw === 'array') return 'array';
  return 'string';
}

function stringifyValue(val) {
  if (val === undefined || val === null) return '';
  if (typeof val === 'object') {
    try { return JSON.stringify(val, null, 2); } catch (e) { return String(val); }
  }
  return String(val);
}

function renderVarValueInput(dataType, nodeId, pv, hasRuntime, runtimeValue, onParamChange, isValueLocked, readOnly) {
  var valueString = pv.value !== undefined && pv.value !== null ? String(pv.value) : '';
  // Locked while the workflow is mid-flight (running / paused / failed) so
  // the live value stays the source of truth and the user can't desync it.
  // Unlocked once the run finishes cleanly — at that point the value is just
  // a stale snapshot the user is free to overwrite for the next run.
  var showRuntimeValue = hasRuntime && isValueLocked;
  var isLocked = readOnly || showRuntimeValue;
  // Only mirror the runtime value while locked. When the field is editable it
  // MUST render `pv.value` — the same field onChange writes to — or every
  // keystroke is instantly reverted by the stale runtime snapshot. The live
  // value stays visible in the row's header badge either way.
  var displayString = showRuntimeValue ? stringifyValue(runtimeValue) : valueString;
  var displayBool = displayString.toLowerCase() === 'true';

  if (dataType === 'bool') {
    return h('button', {
      className: 'vars-panel-switch' + (displayBool ? ' vars-panel-switch-on' : '') + (isLocked ? ' vars-panel-switch-live' : ''),
      type: 'button',
      disabled: isLocked,
      onMouseDown: function(e) {
        e.preventDefault(); e.stopPropagation();
        if (!isLocked) onParamChange(nodeId, 'value', displayBool ? 'false' : 'true');
      }
    },
      h('span', { className: 'vars-panel-switch-track' }, h('span', { className: 'vars-panel-switch-thumb' })),
      h('span', { className: 'vars-panel-switch-text' }, displayBool ? 'True' : 'False')
    );
  }
  if ((dataType === 'json' || dataType === 'array') && showRuntimeValue && runtimeValue && typeof runtimeValue === 'object') {
    return h('div', {
      className: 'vars-panel-tree',
      onMouseDown: function(e) { e.stopPropagation(); }
    }, h(JsonTreeNode, { value: runtimeValue, depth: 0 }));
  }
  if (dataType === 'json' || dataType === 'array') {
    return h('textarea', {
      className: 'vars-panel-textarea' + (isLocked ? ' vars-panel-input-live' : ''),
      placeholder: dataType === 'array' ? '[]' : '{}',
      value: displayString,
      readOnly: isLocked,
      rows: 3,
      onChange: isLocked ? undefined : function(e) { onParamChange(nodeId, 'value', e.target.value); },
      onMouseDown: function(e) { e.stopPropagation(); }
    });
  }
  return h('input', {
    className: 'vars-panel-input' + (isLocked ? ' vars-panel-input-live' : ''),
    type: dataType === 'date' ? 'date' : dataType === 'int' ? 'number' : 'text',
    step: dataType === 'int' ? '1' : undefined,
    placeholder: dataType === 'date' ? 'YYYY-MM-DD' : dataType === 'int' ? '0' : 'value',
    value: displayString,
    readOnly: isLocked,
    onChange: isLocked ? undefined : function(e) { onParamChange(nodeId, 'value', e.target.value); },
    onMouseDown: function(e) { e.stopPropagation(); }
  });
}

function formatVarBubbleValue(value) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') {
    try {
      var s = JSON.stringify(value);
      return s.length > 28 ? s.slice(0, 25) + '...' : s;
    } catch(e) { return String(value); }
  }
  var str = String(value);
  return str.length > 28 ? str.slice(0, 25) + '...' : str;
}

function VariableRowItem(props) {
  var node = props.node;
  var pv = props.pv;
  var dataType = props.dataType;
  var isExpanded = props.isExpanded;
  var hasRuntime = props.hasRuntime;
  var runtimeValue = props.runtimeValue;
  var isStatic = props.isStatic;
  var isRunning = !!props.isRunning;
  var isValueLocked = !!props.isValueLocked;
  var wasAccessed = !!props.wasAccessed;
  var isBeingAccessed = !!props.isBeingAccessed;
  var isUnused = !!props.isUnused;
  var onExpandToggle = props.onExpandToggle;
  var onDelete = props.onDelete;
  var onTitleChange = props.onTitleChange;
  var onParamChange = props.onParamChange;
  var onHoverEnter = props.onHoverEnter;
  var onHoverLeave = props.onHoverLeave;
  var readOnly = !!props.readOnly;
  // 'used' | 'unused' | null. Drives the canvas-node-hover dim: 'unused'
  // rows fade so the user can see at a glance which variables the hovered
  // node touches. null = no node hovered, render normally.
  var nodeHoverState = props.nodeHoverState || null;
  // True when ANOTHER variable row is currently being hovered (focus mode).
  // The hovered row stays full opacity, others fade so the user's attention
  // lands on the one they're pointing at.
  var isOtherHovered = !!props.isOtherHovered;

  // Bump key on each value change to re-trigger slide animation
  var [valueKey, setValueKey] = useState(0);
  // null = closed. 'value' = opened from the Value field's expand button
  // (edits the stored value). 'live' = opened from the header's live badge
  // (shows the runtime snapshot, read-only).
  var [popupMode, setPopupMode] = useState(null);
  var showPopup = popupMode !== null;
  function setShowPopup(open) { setPopupMode(open ? 'value' : null); }
  var [copyState, setCopyState] = useState('idle');
  var prevRuntimeRef = useRef(runtimeValue);

  useEffect(function() {
    if (hasRuntime && runtimeValue !== prevRuntimeRef.current) {
      setValueKey(function(k) { return k + 1; });
    }
    prevRuntimeRef.current = runtimeValue;
  }, [runtimeValue, hasRuntime]);

  // Esc closes the popup. Listener only attaches while open so it doesn't
  // intercept Esc for the rest of the canvas.
  useEffect(function() {
    if (!showPopup) return undefined;
    function onKey(e) { if (e.key === 'Escape') { e.stopPropagation(); setShowPopup(false); } }
    window.addEventListener('keydown', onKey);
    return function() { window.removeEventListener('keydown', onKey); };
  }, [showPopup]);

  // Same rule as the inline input: the runtime snapshot is only shown while
  // the field is read-only. Once editable it must render `pv.value`, the
  // field the textarea's onChange writes to. Opening from the live badge
  // ('live' mode) always shows the runtime snapshot — that's the value the
  // badge was previewing — and stays read-only for the same reason.
  var showRuntimePopupValue = hasRuntime && (isValueLocked || popupMode === 'live');
  var isPopupLocked = readOnly || showRuntimePopupValue;

  function popupBodyText() {
    return showRuntimePopupValue
      ? stringifyValue(runtimeValue)
      : (pv.value !== undefined && pv.value !== null ? String(pv.value) : '');
  }

  // Pick the structured value to render in the colored tree, when the
  // variable is json/array. Prefer the live runtime value; fall back to
  // parsing the static `pv.value` (which is stored as a JSON string in the
  // editor). Returns null when no usable structured value is available —
  // the popup then falls back to the plain editable textarea.
  function popupTreeValue() {
    if (dataType !== 'json' && dataType !== 'array') return null;
    if (isPopupLocked && runtimeValue && typeof runtimeValue === 'object') return runtimeValue;
    var raw = pv.value;
    if (raw === undefined || raw === null) return null;
    if (typeof raw === 'object') return raw;
    var trimmed = String(raw).trim();
    if (!trimmed || (trimmed[0] !== '{' && trimmed[0] !== '[')) return null;
    try {
      var parsed = JSON.parse(trimmed);
      return (parsed && typeof parsed === 'object') ? parsed : null;
    } catch (_e) {
      return null;
    }
  }

  function copyPopupValue() {
    var text = popupBodyText();
    if (!text) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(function() {
          setCopyState('copied');
          setTimeout(function() { setCopyState('idle'); }, 1200);
        }).catch(function() { setCopyState('idle'); });
      }
    } catch (_e) { setCopyState('idle'); }
  }

  return h('div', {
    className: 'vars-panel-item'
      + (isExpanded ? ' vars-panel-item-open' : '')
      + (hasRuntime ? ' vars-panel-item-live' : '')
      + (wasAccessed && !isBeingAccessed ? ' vars-panel-item-accessed' : '')
      + (nodeHoverState === 'unused' ? ' vars-panel-item-node-hover-dim' : '')
      + (nodeHoverState === 'used' ? ' vars-panel-item-node-hover-used' : '')
      + (isOtherHovered ? ' vars-panel-item-other-hovered' : ''),
    onMouseEnter: onHoverEnter,
    onMouseLeave: onHoverLeave
  },
    h('div', {
      className: 'vars-panel-item-header',
      onClick: onExpandToggle
    },
      h('span', { className: 'vars-panel-type-badge ' + (VAR_TYPE_COLORS[dataType] || 'vars-type-text') },
        VAR_TYPE_LABELS[dataType] || 'Text'
      ),
      h('span', { className: 'vars-panel-item-name' }, node.title || 'Unnamed'),
      hasRuntime ? h('button', {
        key: valueKey,
        type: 'button',
        className: 'vars-live-value-badge',
        title: 'Click to open the full value',
        // Opens the same expanded viewer as the value field's expand button,
        // without toggling the row open/closed.
        onMouseDown: function(e) { e.stopPropagation(); },
        onClick: function(e) { e.stopPropagation(); setPopupMode('live'); }
      }, formatVarBubbleValue(runtimeValue)) : null,
      isUnused ? h('span', {
        className: 'vars-panel-unused-badge',
        title: 'This variable is never read or written by any node'
      }, '!') : null,
      isStatic ? h('span', { className: 'vars-panel-static-badge', title: 'Static: persists across runs' }, 'S') : null,
      isBeingAccessed ? h('span', { className: 'vars-panel-active-dot', title: 'Being read by running node', style: { marginLeft: 'auto' } }) : null,
      readOnly ? null : h('button', {
        className: 'vars-panel-delete-btn',
        onClick: function(e) { e.stopPropagation(); onDelete(node.id, e); },
        title: 'Delete variable',
        onMouseDown: function(e) { e.stopPropagation(); }
      }, '\u00d7')
    ),
    isExpanded ? h('div', { className: 'vars-panel-item-body' },
      h('label', { className: 'vars-panel-field' },
        h('span', { className: 'vars-panel-field-label' }, 'Name'),
        h('input', {
          className: 'vars-panel-input',
          value: node.title || '',
          readOnly: readOnly,
          onChange: readOnly ? undefined : function(e) { onTitleChange(node.id, e.target.value); },
          onMouseDown: function(e) { e.stopPropagation(); },
          placeholder: 'Variable name'
        })
      ),
      h('label', { className: 'vars-panel-field' },
        h('span', { className: 'vars-panel-field-label' }, 'Type'),
        h('select', {
          className: 'vars-panel-select',
          value: dataType,
          disabled: readOnly,
          onChange: readOnly ? undefined : function(e) { onParamChange(node.id, 'dataType', e.target.value); },
          onMouseDown: function(e) { e.stopPropagation(); }
        },
          h('option', { value: 'string' }, 'Text'),
          h('option', { value: 'int' }, 'Number'),
          h('option', { value: 'date' }, 'Date'),
          h('option', { value: 'bool' }, 'True / False'),
          h('option', { value: 'json' }, 'Object'),
          h('option', { value: 'array' }, 'List')
        )
      ),
      h('div', { className: 'vars-panel-field' },
        h('div', { className: 'vars-panel-field-label-row' },
          h('span', { className: 'vars-panel-field-label' }, hasRuntime ? 'Value \u25cf' : 'Value'),
          dataType !== 'bool'
            ? h('button', {
                type: 'button',
                className: 'vars-panel-expand-btn',
                title: 'Open in expanded editor',
                onMouseDown: function(e) { e.stopPropagation(); },
                onClick: function(e) { e.stopPropagation(); setShowPopup(true); }
              }, '\u26f6')
            : null
        ),
        renderVarValueInput(dataType, node.id, pv, hasRuntime, runtimeValue, onParamChange, isValueLocked, readOnly)
      ),
      // List auto-trim: keep only the newest N items. Empty = never trim.
      dataType === 'array' ? h('label', { className: 'vars-panel-field' },
        h('span', { className: 'vars-panel-field-label' }, 'Keep last'),
        h('input', {
          className: 'vars-panel-input',
          type: 'number',
          min: '1',
          step: '1',
          value: pv.keepLast || '',
          readOnly: readOnly,
          placeholder: 'All items (no trim)',
          title: 'Auto-trim the list to its newest N items. Leave empty to keep everything.',
          onChange: readOnly ? undefined : function(e) { onParamChange(node.id, 'keepLast', e.target.value); },
          onMouseDown: function(e) { e.stopPropagation(); }
        })
      ) : null,
      h('div', { className: 'vars-panel-field' },
        h('span', { className: 'vars-panel-field-label' }, 'Mode'),
        h('div', { className: 'vars-panel-mode-row' },
          h('button', {
            className: 'vars-panel-mode-btn' + (!isStatic ? ' vars-panel-mode-btn-active' : ''),
            disabled: readOnly,
            onMouseDown: function(e) { e.preventDefault(); e.stopPropagation(); if (!readOnly) onParamChange(node.id, 'retain', 'false'); },
            title: 'Value resets each run'
          }, 'Temporary'),
          h('button', {
            className: 'vars-panel-mode-btn' + (isStatic ? ' vars-panel-mode-btn-active' : ''),
            disabled: readOnly,
            onMouseDown: function(e) { e.preventDefault(); e.stopPropagation(); if (!readOnly) onParamChange(node.id, 'retain', 'true'); },
            title: 'Value persists across runs'
          }, 'Persistent')
        )
      )
    ) : null,
    showPopup ? ReactDOM.createPortal(
      h('div', {
        className: 'vars-panel-expand-overlay',
        onMouseDown: function(e) { e.stopPropagation(); },
        onClick: function(e) { if (e.target === e.currentTarget) setShowPopup(false); }
      },
        h('div', { className: 'vars-panel-expand-modal' },
          h('div', { className: 'vars-panel-expand-header' },
            h('div', { className: 'vars-panel-expand-title' },
              h('span', { className: 'vars-panel-type-badge ' + (VAR_TYPE_COLORS[dataType] || 'vars-type-text') },
                VAR_TYPE_LABELS[dataType] || 'Text'
              ),
              h('span', { className: 'vars-panel-expand-name' }, node.title || 'Variable'),
              hasRuntime ? h('span', { className: 'vars-panel-expand-live' }, 'LIVE') : null,
              isPopupLocked ? h('span', { className: 'vars-panel-expand-readonly' }, 'read-only') : null
            ),
            h('div', { className: 'vars-panel-expand-actions' },
              h('button', {
                type: 'button',
                className: 'vars-panel-expand-action' + (copyState === 'copied' ? ' is-copied' : ''),
                title: 'Copy value to clipboard',
                onClick: copyPopupValue
              }, copyState === 'copied' ? 'Copied' : 'Copy'),
              h('button', {
                type: 'button',
                className: 'vars-panel-expand-close',
                title: 'Close (Esc)',
                onClick: function() { setShowPopup(false); }
              }, '\u00d7')
            )
          ),
          (function() {
            var treeValue = popupTreeValue();
            if (treeValue !== null) {
              return h('div', {
                className: 'vars-panel-expand-body vars-panel-expand-tree',
                onMouseDown: function(e) { e.stopPropagation(); }
              }, h(JsonTreeNode, { value: treeValue, depth: 0 }));
            }
            return h('textarea', {
              className: 'vars-panel-expand-body vars-panel-expand-textarea',
              value: popupBodyText(),
              placeholder: dataType === 'array' ? '[]' : (dataType === 'json' ? '{}' : 'value'),
              autoFocus: true,
              spellCheck: false,
              readOnly: isPopupLocked,
              onChange: isPopupLocked ? undefined : function(e) { onParamChange(node.id, 'value', e.target.value); },
              onMouseDown: function(e) { e.stopPropagation(); }
            });
          })()
        )
      ),
      document.body
    ) : null
  );
}

function VariablesSidePanel(props) {
  var variables = props.variables || [];
  var variableValuesByName = props.variableValuesByName || {};
  var accessedVariableNames = props.accessedVariableNames || {};
  var accessedVariableNodeId = props.accessedVariableNodeId || {};
  var nodeStatusById = props.nodeStatusById || {};
  var onAdd = props.onAdd || function() {};
  var onParamChange = props.onParamChange || function() {};
  var onTitleChange = props.onTitleChange || function() {};
  var onDelete = props.onDelete || function() {};
  var readOnly = !!props.readOnly;
  var collapsed = !!props.collapsed;
  var onToggleCollapse = props.onToggleCollapse || function() {};
  var isRunning = !!props.isRunning;
  // Locks the value field while the workflow is mid-flight (running, paused,
  // or failed). Once the run finishes cleanly the field becomes editable
  // again. Falls back to isRunning for callers that haven't migrated yet.
  var isValueLocked = props.isValueLocked != null ? !!props.isValueLocked : isRunning;
  var onVariableHover = typeof props.onVariableHover === 'function' ? props.onVariableHover : null;
  // Set of variable names touched by the canvas-side node currently being
  // hovered. null = no node hovered, render every row normally. When set,
  // rows whose title isn't in the map are dimmed, used rows are emphasized.
  var highlightedVariableNames = props.highlightedVariableNames || null;
  // Map of variable title -> true for variables not referenced by any node.
  // Drives the yellow exclamation badge on unused rows.
  var unusedVariableNames = props.unusedVariableNames || {};

  var _s = useState(null);
  var expandedId = _s[0];
  var setExpandedId = _s[1];
  var touchScrollRef = useRef(null);

  // Local row-hover state — drives the in-panel "focus the hovered row, dim
  // the rest" effect. Updates instantly on mouseenter/leave (no debounce),
  // so the visual feedback lands the moment the cursor enters a row even
  // though the canvas pan/zoom waits 250ms for intent.
  var _h = useState(null);
  var hoveredRowName = _h[0];
  var setHoveredRowName = _h[1];

  // Tracks whether ANY input/textarea/select inside the panel currently has
  // focus. Backs the `vars-panel-editing` class which suppresses the
  // hover-fade / see-through-chrome effect while the user is editing — pure
  // CSS `:focus-within` is unreliable across browsers when combined with
  // `:has()`, so we mirror the state in JS via bubbled focus/blur events.
  var _e = useState(false);
  var isEditing = _e[0];
  var setIsEditing = _e[1];

  function isEditableTarget(el) {
    if (!el) return false;
    var tag = el.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
  }
  function onPanelFocusIn(e) {
    if (isEditableTarget(e.target)) setIsEditing(true);
  }
  function onPanelFocusOut(e) {
    if (!isEditableTarget(e.target)) return;
    var next = e.relatedTarget;
    if (next && e.currentTarget && e.currentTarget.contains(next)) return;
    setIsEditing(false);
  }

  // Two-sided debounce on hover signals reaching the canvas:
  //   - ENTER: 250ms intent delay. Quickly grazing across multiple rows on
  //     the way to a target row should NOT pan/zoom for each row in passing.
  //     Only the row the cursor actually settles on for ~quarter-second
  //     fires the hover.
  //   - LEAVE: 600ms grace period so the user can move the cursor off the
  //     row onto a highlighted node (or its chip) without the highlight
  //     tearing down.
  // The two timers share state because an enter while a leave is pending
  // (or vice versa) must cancel the prior one.
  var hoverEnterTimerRef = useRef(null);
  var hoverLeaveTimerRef = useRef(null);
  // Tracks whether a hover is currently committed to the parent. Used to
  // pick the enter delay (slow first-time, fast row-to-row) — NOT for
  // deduplication. The parent's `hoveredVariableName` state is the source
  // of truth; the canvas effect handles the same-name no-op via React's
  // state equality check.
  var hoverActiveRef = useRef(false);

  function clearHoverTimers() {
    if (hoverEnterTimerRef.current) {
      clearTimeout(hoverEnterTimerRef.current);
      hoverEnterTimerRef.current = null;
    }
    if (hoverLeaveTimerRef.current) {
      clearTimeout(hoverLeaveTimerRef.current);
      hoverLeaveTimerRef.current = null;
    }
  }

  function emitVariableHover(name) {
    if (!onVariableHover) return;
    clearHoverTimers();

    if (name === null) {
      hoverActiveRef.current = false;
      onVariableHover(null);
      return;
    }

    hoverActiveRef.current = true;
    onVariableHover(name);
  }

  // Cancel pending timers on unmount so a stale one can't fire after the
  // panel is gone.
  useEffect(function() {
    return function() { clearHoverTimers(); };
  }, []);

  if (variables.length === 0) return null;

  function stopPanelTouchBubble(event) {
    event.stopPropagation();
  }

  function onListTouchStart(event) {
    event.stopPropagation();
    if (!event.touches || event.touches.length !== 1) {
      touchScrollRef.current = null;
      return;
    }
    var touch = event.touches[0];
    touchScrollRef.current = {
      startY: touch.clientY,
      startScrollTop: event.currentTarget.scrollTop
    };
  }

  function onListTouchMove(event) {
    event.stopPropagation();
    var state = touchScrollRef.current;
    if (!state || !event.touches || event.touches.length !== 1) return;
    var touch = event.touches[0];
    var deltaY = touch.clientY - state.startY;
    event.currentTarget.scrollTop = state.startScrollTop - deltaY;
    event.preventDefault();
  }

  function onListTouchEnd(event) {
    event.stopPropagation();
    touchScrollRef.current = null;
  }

  return h('div', {
    className: 'vars-panel'
      + (collapsed ? ' vars-panel-collapsed' : '')
      + (isEditing ? ' vars-panel-editing' : ''),
    onMouseDown: function(e) { e.stopPropagation(); },
    onFocus: onPanelFocusIn,
    onBlur: onPanelFocusOut,
    onTouchStart: stopPanelTouchBubble,
    onTouchMove: stopPanelTouchBubble,
    onTouchEnd: stopPanelTouchBubble
  },
    h('div', { className: 'vars-panel-header' },
      h('span', { className: 'vars-panel-title' },
        h('svg', { width: 13, height: 13, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.2, strokeLinecap: 'round', strokeLinejoin: 'round', style: { marginRight: 5, verticalAlign: 'middle', opacity: 0.7 } },
          h('path', { d: 'M4 7h16M4 12h16M4 17h10' })
        ),
        'Variable'
      ),
      h('div', { className: 'vars-panel-header-actions' },
        h('button', {
          className: 'vars-panel-toggle-btn',
          onClick: onToggleCollapse,
          title: collapsed
            ? 'Expand variables panel'
            : (isRunning ? 'Minimize (will auto-show while workflow runs)' : 'Minimize variables panel')
        },
          h('svg', {
            width: 12,
            height: 12,
            viewBox: '0 0 24 24',
            fill: 'none',
            stroke: 'currentColor',
            strokeWidth: 2.6,
            strokeLinecap: 'round',
            strokeLinejoin: 'round',
            'aria-hidden': 'true'
          },
            collapsed
              ? h('path', { d: 'M6 9l6 6 6-6' })
              : h('path', { d: 'M6 15l6-6 6 6' })
          )
        ),
        readOnly ? null : h('button', {
          className: 'vars-panel-add-btn',
          onClick: onAdd,
          title: 'Add variable'
        }, '+')
      )
    ),
    !collapsed
      ? h('div', {
          className: 'vars-panel-list',
          onTouchStart: onListTouchStart,
          onTouchMove: onListTouchMove,
          onTouchEnd: onListTouchEnd,
          onTouchCancel: onListTouchEnd
        },
          variables.map(function(node) {
            var pv = node.paramValues || {};
            var dataType = normalizeVarType(pv.dataType);
            var isExpanded = expandedId === node.id;
            var varName = String(node.title || '').trim();
            var hasRuntime = varName && Object.prototype.hasOwnProperty.call(variableValuesByName, varName);
            var runtimeValue = hasRuntime ? variableValuesByName[varName] : null;
            var isStatic = String(pv.retain) === 'true';
            var wasAccessed = !!(varName && accessedVariableNames[varName]);
            var accessingNodeId = varName && accessedVariableNodeId[varName];
            var isBeingAccessed = !!(wasAccessed && accessingNodeId && nodeStatusById[accessingNodeId] === 'running');

            return h(VariableRowItem, {
              key: node.id,
              node: node,
              pv: pv,
              dataType: dataType,
              isExpanded: isExpanded,
              hasRuntime: hasRuntime,
              runtimeValue: runtimeValue,
              isStatic: isStatic,
              isRunning: isRunning,
              isValueLocked: isValueLocked,
              wasAccessed: wasAccessed,
              isBeingAccessed: isBeingAccessed,
              isUnused: !!(varName && unusedVariableNames[varName]),
              onExpandToggle: function() { setExpandedId(isExpanded ? null : node.id); },
              onDelete: onDelete,
              onTitleChange: onTitleChange,
              onParamChange: onParamChange,
              readOnly: readOnly,
              onHoverEnter: function() {
                setHoveredRowName(varName);
                if (onVariableHover) emitVariableHover(varName);
              },
              onHoverLeave: function() {
                setHoveredRowName(function(prev) { return prev === varName ? null : prev; });
                if (onVariableHover) emitVariableHover(null);
              },
              isOtherHovered: hoveredRowName != null && hoveredRowName !== varName,
              nodeHoverState: highlightedVariableNames
                ? (highlightedVariableNames[varName] ? 'used' : 'unused')
                : null
            });
          })
        )
      : null
  );
}

module.exports = VariablesSidePanel;
